import _ from 'lodash';
import { CopyNumberTypes, ObservationWithTask } from './types';
import { NOTES_EXTENSION_URL, TRIAL_ID_CODING } from './constants';
import { TaskOutputIn } from '../../../../../util/graphql/api.graphql';

export const getVariantType = (
  observation: fhir.Observation,
): string | undefined => {
  return observation.extension?.find(
    ({ url }) =>
      url === 'http://hl7.org/fhir/StructureDefinition/variant-set-type',
  )?.valueString;
};

export const getGene = (observation: fhir.Observation): string | undefined => {
  const targetGenes = observation.component
    ?.filter(component =>
      component.code?.coding?.find(
        coding =>
          coding.code === '48018-6' && coding.system === 'http://loinc.org',
      ),
    )
    ?.map(component =>
      component.valueCodeableConcept?.coding?.map(coding => coding.code),
    )
    ?.flat()
    ?.filter(Boolean);
  if (!targetGenes?.length) {
    return undefined;
  }
  return targetGenes.join('-');
};

export const getAaChange = (
  observation: fhir.Observation,
): string | undefined => {
  const aaChange = observation.component?.find(component =>
    component.code?.coding?.find(
      coding =>
        coding.code === '53721-7' && coding.system === 'http://loinc.org',
    ),
  );
  if (!aaChange?.valueString || aaChange.valueString === 'unknown') {
    return undefined;
  }
  return aaChange.valueString;
};

export const getNcChange = (
  observation: fhir.Observation,
): string | undefined => {
  const ncChange = observation.component?.find(component =>
    component.code?.coding?.find(
      coding =>
        coding.code === '48004-6' && coding.system === 'http://loinc.org',
    ),
  );
  if (
    !ncChange?.valueCodeableConcept?.coding?.[0]?.code ||
    ncChange?.valueCodeableConcept?.coding?.[0]?.code === 'unknown'
  ) {
    return undefined;
  }
  if (ncChange.valueCodeableConcept.coding[0].code.includes(':')) {
    return ncChange.valueCodeableConcept.coding[0].code.split(':')[1];
  }
  return ncChange.valueCodeableConcept.coding[0].code;
};

export const getAlleleFrequency = (
  observation: fhir.Observation,
): string | undefined => {
  const alleleFrequency = observation.component?.find(component =>
    component.code?.coding?.find(
      coding =>
        coding.code === '81258-6' && coding.system === 'http://loinc.org',
    ),
  );
  if (alleleFrequency?.valueQuantity?.value === undefined) {
    return undefined;
  }
  return `${alleleFrequency?.valueQuantity?.value.toFixed(2)}`;
};

export const getEffect = (
  observation: fhir.Observation,
): string | undefined => {
  const effect = observation.component?.find(component =>
    component.code?.coding?.find(
      coding =>
        coding.code === '48019-4' && coding.system === 'http://loinc.org',
    ),
  );

  const effectDisplay = effect?.valueCodeableConcept?.coding?.[0]?.display;

  if (!effectDisplay) {
    return undefined;
  }

  return effectDisplay.toLowerCase();
};

export const getCopyNumberValue = (
  observation: fhir.Observation,
): string | undefined => {
  const cnValue = observation.component?.find(component =>
    component.code?.coding?.find(
      coding =>
        coding.code === '82155-3' && coding.system === 'http://loinc.org',
    ),
  );
  if (cnValue?.valueQuantity?.value === undefined) {
    return undefined;
  }
  return `${cnValue?.valueQuantity?.value.toFixed(1)}`;
};

export const getCopyNumberType = (
  observation: fhir.Observation,
): CopyNumberTypes | undefined => {
  // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
  const type = observation.component.find(component =>
    component.code?.coding?.find(
      coding =>
        coding.code === 'Copy Number Type' &&
        coding.system === 'http://lifeomic.com/fhir/copyNumberType',
    ),
  );
  // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
  if (type?.valueCodeableConcept?.coding[0]?.code === undefined) {
    return undefined;
  }
  return type?.valueCodeableConcept?.coding[0]?.code as CopyNumberTypes;
};

export const getExpressionValueAndUnit = (
  observation: fhir.Observation,
):
  | {
      value: string;
      unit: string;
    }
  | undefined => {
  const expression = observation.component?.find(component =>
    component.code?.coding?.find(
      coding =>
        coding.code === 'Expression Value' &&
        coding.system === 'http://lifeomic.com/fhir/expressionValue',
    ),
  );
  if (expression?.valueQuantity?.value === undefined) {
    return undefined;
  }
  return {
    value: expression?.valueQuantity.value.toFixed(2),
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    unit: expression?.valueQuantity.code.toLocaleUpperCase() || 'TPM',
  };
};

export const getRecommendation = (
  observation: ObservationWithTask | undefined,
) => {
  // TODO filter string to avoid direct string injection;
  return observation?.task?.note?.[0]?.text;
};

export const getNotes = (observation: ObservationWithTask | undefined) => {
  return observation?.extension?.find(
    (extension: fhir.Extension) => extension.url === NOTES_EXTENSION_URL,
  )?.valueAnnotation?.text;
};

export const setNotes = (
  observation: ObservationWithTask,
  notes: string,
): ObservationWithTask => {
  const updated = _.cloneDeep(observation);
  const notesExtension = updated?.extension?.find(
    (extension: fhir.Extension) => extension.url === NOTES_EXTENSION_URL,
  );
  if (!notesExtension) {
    // New notes
    if (!updated.extension) {
      updated.extension = [];
    }
    updated.extension.push({
      url: NOTES_EXTENSION_URL,
      valueAnnotation: {
        text: notes,
      },
    });
  } else {
    // Updated notes
    notesExtension.valueAnnotation = {
      text: notes,
    };
  }
  return updated;
};

export const getTrialId = (observation: ObservationWithTask | undefined) => {
  return observation?.task?.output?.find(
    _ => _.type?.coding?.[0]?.code === TRIAL_ID_CODING.code,
  )?.valueString;
};

export const setTrialId = (
  observation: ObservationWithTask,
  trialId: string,
) => {
  const updated = _.cloneDeep(observation);
  const trialComponent = updated?.component?.find(
    component => component.code?.coding?.[0]?.code === TRIAL_ID_CODING.code,
  );
  if (!trialComponent) {
    // New trial id
    if (!updated.component) {
      updated.component = [];
    }
    updated.component.push({
      code: {
        coding: [TRIAL_ID_CODING],
      },
      valueString: trialId,
    });
  } else {
    // Updated trial id
    trialComponent.valueString = trialId;
  }
  return updated;
};

export const getTaskOutput = (trialId: string): TaskOutputIn[] => [
  {
    type: {
      coding: [TRIAL_ID_CODING],
    },
    valueString: trialId,
  },
];

// return true of given object is a selected variant
export const isTargetedVariant = (code: fhir.CodeableConcept): boolean => {
  // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
  return !!code.coding.find(
    (coding: fhir.Coding) =>
      coding.code === '69548-6' && coding.system === 'http://loinc.org',
  );
};

export const isManualFinding = (
  observation: fhir.Observation,
  status?: string,
): boolean => {
  return (
    !!observation?.code?.coding?.find(
      (coding: fhir.Coding) =>
        coding.code === '51968-6' && coding.system === 'http://loinc.org',
    ) &&
    (observation.status === status || !status)
  );
};

// return true of given extension is a recommendation
export const isRecommendation = (extension: fhir.Extension) =>
  extension.url ===
  'http://hl7.org/fhir/uv/genomics-reporting/StructureDefinition/recommended-action';

export const createTaskReference = (taskId: string) => ({
  url: 'http://hl7.org/fhir/uv/genomics-reporting/StructureDefinition/recommended-action',
  valueReference: {
    reference: `Task/${taskId}`,
  },
});

export const findManualFinding = (
  observation: fhir.Observation,
): string | undefined => {
  if (!isManualFinding(observation)) {
    return undefined;
  }

  const component = observation?.component?.find(
    component =>
      !!component?.code?.coding?.find(
        ({ system, code }) =>
          system ===
            'http://hl7.org/fhir/uv/genomics-reporting/CodeSystem/tbd-codes-cs' &&
          code === 'conclusion-string',
      ),
  );
  return component?.valueString || undefined;
};

export const createManualFindingObservation = (
  id: string,
  subjectId: string,
  datasetId: string,
  finding: string,
  status: string = 'preliminary',
) => ({
  id: id,
  status,
  resourceType: 'Observation',
  subject: { reference: `Patient/${subjectId}` },
  meta: {
    tag: [
      { code: datasetId, system: 'http://lifeomic.com/fhir/dataset' },
      {
        code: 'LifeOmic Variant Selection',
        system: 'http://lifeomic.com/fhir/source',
      },
    ],
  },
  code: {
    coding: [
      {
        code: '51968-6',
        system: 'http://loinc.org',
        display: 'Discrete variation analysis overall interpretation',
      },
    ],
  },
  component: [
    {
      code: {
        coding: [
          {
            system:
              'http://hl7.org/fhir/uv/genomics-reporting/CodeSystem/tbd-codes-cs',
            code: 'conclusion-string',
          },
        ],
      },
      valueString: finding,
    },
  ],
});

export const omitDeep = (
  resource: Record<string, any> | undefined,
  propertyName: string,
): void => {
  for (const prop of Object.keys(resource || {})) {
    if (prop === propertyName) {
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      delete resource[prop];
    }
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    if (typeof resource[prop] === 'object') {
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      omitDeep(resource[prop], propertyName);
    }
  }
};

export const setEvidenceLevelValue = (
  observation: ObservationWithTask,
  evidenceLevel: fhir.Coding,
  evidenceLevelValue: fhir.Coding,
): ObservationWithTask => {
  const updated: fhir.Observation = _.cloneDeep(observation);
  omitDeep(updated, '__typename');
  const component = updated.component?.find(
    ({ code }) =>
      code?.coding?.[0].code === evidenceLevel.code &&
      code?.coding?.[0].system === evidenceLevel.system,
  );

  // Update component if it exists:
  if (component) {
    component.valueCodeableConcept = {
      coding: [{ ...evidenceLevelValue }],
    };
    return updated;
  }

  // No evidence level set yet, add one now:
  if (!updated.component) {
    updated.component = [];
  }
  updated.component.push({
    code: {
      coding: [
        {
          ...evidenceLevel,
        },
      ],
    },
    valueCodeableConcept: {
      coding: [
        {
          ...evidenceLevelValue,
        },
      ],
    },
  });
  return updated;
};

export const getEvidenceLevelValue = (
  observation: ObservationWithTask,
  evidenceLevel: fhir.Coding,
): fhir.Coding | null => {
  const component = (observation?.component || []).find(
    ({ code }) =>
      code?.coding?.[0].code === evidenceLevel.code &&
      code?.coding?.[0].system === evidenceLevel.system,
  );
  if (!component?.valueCodeableConcept?.coding?.[0]) {
    return null;
  }
  return _.pick(component.valueCodeableConcept.coding[0], [
    'code',
    'system',
    'display',
  ]);
};
